import { getDispatchedFilePath, getMediaSourceUrlKey } from '@/shared/functions';
import type { MediaSource } from '@/shared/types';

/**
 * Identity keys for the media a viewer has loaded.
 *
 * Two flavours, and the difference matters:
 *
 * - {@link getMediaDisplayIdentityKey} — the underlying *file*. Two citations
 *   of one document share it.
 * - {@link getMediaDisplayClipIdentityKey} — the *citation*: file plus cited
 *   page. Distinct passages of one document do not share it, which is what
 *   viewing-page state is keyed by.
 * - {@link getMediaDisplayCaptureIdentityKey} — the citation instance in an
 *   answer plus its clip. Capture state uses this so later answers citing the
 *   same file and page cannot inherit an earlier answer's crop (TR-3104).
 *
 * Lives outside ``mediaDisplay.tsx`` so these pure functions have a direct
 * unit-test surface — see ``mediaIdentityKeys.test.ts``.
 */

export const isMbox = (url: string | undefined) => {
  return url?.endsWith('.mbox') || url?.endsWith('.mbx');
};

function pathEndsWithEml(url: string | undefined): boolean {
  if (!url?.trim()) return false;
  const path = url.split('?')[0].split('#')[0].toLowerCase();
  return path.endsWith('.eml');
}

/**
 * The cited mailbox member, when the row already names an extracted ``.eml``.
 * Chat citations set ``sourceContext.email_index`` to 0 for every message
 * (TR-4890), so that field cannot identify the member.
 */
export function citedEmlUrl(media: MediaSource | undefined): string | null {
  if (!media) return null;
  const fromContext = media.mediaContext as { dispatchedFilePath?: unknown; dispatched_file_path?: unknown } | undefined;
  const contextDispatched =
    typeof fromContext?.dispatchedFilePath === 'string'
      ? fromContext.dispatchedFilePath
      : typeof fromContext?.dispatched_file_path === 'string'
        ? fromContext.dispatched_file_path
        : '';
  const candidates = [getDispatchedFilePath(media), contextDispatched, media.mediaUrl, media.mediaSrc, media.sourceFilePath];
  for (const candidate of candidates) {
    if (typeof candidate === 'string' && pathEndsWithEml(candidate)) return candidate.trim();
  }
  return null;
}

/**
 * True when the pane already has the cited member (an ``.eml`` or a body URL
 * that is not the mailbox container). Calling junkdrawer ``/mbox`` with
 * ``email_index`` would open email_0 instead (TR-4890).
 */
export function mboxMemberAlreadyResolved(media: MediaSource | undefined): boolean {
  if (!media?.originalUrl || !isMbox(media.originalUrl)) return false;
  if (citedEmlUrl(media)) return true;
  const display = (media.mediaUrl || media.mediaSrc || media.previewFilePath || '').trim();
  return display !== '' && !isMbox(display);
}

export function parseMediaContextEmailIndex(
  mediaContext: MediaSource['mediaContext'] | undefined,
): number | null {
  if (!mediaContext) return null;
  const ctx = mediaContext as Record<string, unknown>;
  const raw = ctx.email_index ?? ctx.emailIndex;
  if (raw === undefined || raw === null || raw === '') return null;
  const parsed = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : null;
}

/**
 * For mbox items, props often keep the container .mbox URL while resolved state uses a per-email .eml URL,
 * so getMediaSourceUrlKey(parent) !== getMediaSourceUrlKey(resolved). Use the same identity as the mbox fetch
 * effect (currentEmailId) so updates that only touch mediaContext (e.g. capture apply) do not reset the viewer.
 */
export function getMboxEmailIdentityKey(media: MediaSource | undefined): string {
  if (!media?.originalUrl || !isMbox(media.originalUrl)) return '';
  const eml = citedEmlUrl(media);
  if (eml) return eml;
  const display = (media.mediaUrl || media.mediaSrc || media.previewFilePath || '').trim();
  if (display && !isMbox(display)) return display;
  const citedEmailIndex = parseMediaContextEmailIndex(media.mediaContext);
  if (citedEmailIndex !== null) {
    return `${media.originalUrl}_${citedEmailIndex}`;
  }
  const previewUrl = media.mediaContext?.previewUrl || '';
  let currentEmailId = previewUrl;
  if (previewUrl) {
    const parsedEmailIndex = previewUrl?.split('/').pop()?.split('.')[0]?.split('_')[1];
    if (!!parsedEmailIndex && !Number.isNaN(parseInt(parsedEmailIndex))) {
      currentEmailId = `${media.originalUrl || ''}_${parsedEmailIndex}`;
    } else {
      currentEmailId = previewUrl;
    }
  } else {
    currentEmailId = `${media.originalUrl || ''}_0`;
  }
  return currentEmailId;
}

export function getMediaDisplayIdentityKey(media: MediaSource | undefined): string {
  if (!media) return '';
  if (isMbox(media.originalUrl)) {
    return getMboxEmailIdentityKey(media);
  }
  return getMediaSourceUrlKey(media);
}

/**
 * Prop-stable source identity: URL plus page.
 *
 * Ignores enrichment that MediaDisplay writes after mount: ``fileInfo`` (its
 * ``previewUrl``) and, when the row already has ``mediaUrl`` / ``mediaSrc``,
 * the resolved ``srcToUse``. ``getMediaSourceUrlKey`` prefers ``srcToUse``, so
 * a timeline video whose canonical ``sourceUrl`` differs from the playable
 * ``mediaUrl`` used to fail clip alignment and sit on "Loading…" forever
 * (TR-3842). Source previews omit that canonical path and were unaffected.
 * When ``srcToUse`` is the only URL, it stays in the key.
 *
 * Intentionally excludes start/end trim times. Including them remounted MediaPlayer on every
 * scrub of the edit-mode handles (new clip key → ``setItemToUse(undefined)`` → lost
 * ``isEditingMediaContext``). Trim window changes are synced into ``itemToUse`` in place.
 * SourceCard still remounts citation switches via its own React ``key`` that includes startTime.
 */
export function getMediaDisplayClipIdentityKey(media: MediaSource | undefined): string {
  if (!media) return '';
  if (isMbox(media.originalUrl)) {
    return getMboxEmailIdentityKey(media);
  }
  const hasStableUrl = Boolean(media.mediaSrc?.trim() || media.mediaUrl?.trim());
  const urlKey = getMediaSourceUrlKey({
    ...media,
    fileInfo: undefined,
    ...(hasStableUrl ? { srcToUse: undefined } : {}),
  });
  const ctx = {
    ...(media.sourceContext ?? {}),
    ...(media.mediaContext ?? {}),
  };
  const parts: string[] = [urlKey];
  const pageNumber = ctx.pageNumber ?? ctx.page ?? media.pageNumber;
  if (pageNumber != null && pageNumber !== '' && Number(pageNumber) > 0) {
    parts.push(`p:${pageNumber}`);
  }
  return parts.join('|');
}

export function getMediaDisplayCaptureIdentityKey(media: MediaSource | undefined): string {
  const clipKey = getMediaDisplayClipIdentityKey(media);
  if (!clipKey) return '';
  const scope = media?.captureCitationScope?.trim();
  return scope ? `${scope}|${clipKey}` : clipKey;
}

export function getAnswerSourceCaptureScope(args: {
  caseId?: string | null;
  epochTimeSecs: number;
  answerIndex: number;
  sourceIndex: number;
}): string {
  return [
    `case:${args.caseId?.trim() ?? ''}`,
    `answer:${args.epochTimeSecs}:${args.answerIndex}`,
    `source:${args.sourceIndex}`,
  ].join('|');
}
